import {
  ActiveFilterChip,
  FilterField,
  FilterResult,
  FiltersConfig,
  FilterValues,
} from '../models/filter.interface';
import { DictionaryDTO } from 'src/app/core/models/dictionary';

export type FilterChipValue =
  | string
  | number
  | string[]
  | (string | number)[];

type FiltersConfigLike = Pick<FiltersConfig, 'filters' | 'grouping'>;
type FilterResultLike = Pick<FilterResult, 'filters' | 'count'>;

function formatMonthLabelFromValue(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const raw = value.trim();
  if (!raw) {
    return null;
  }

  const yyyyMmDd = /^(\d{4})-(\d{2})-(\d{2})$/;
  const ddMmYyyy = /^(\d{2})\.(\d{2})\.(\d{4})$/;

  const buildLabel = (year: number, monthIndex: number): string | null => {
    if (!Number.isInteger(year) || year < 1900) return null;
    if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return null;
    }

    const date = new Date(year, monthIndex, 1);
    const formatted = new Intl.DateTimeFormat('ru-RU', {
      month: 'long',
      year: 'numeric',
    }).format(date);

    const normalized =
      formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return normalized;
  };

  const isoMatch = yyyyMmDd.exec(raw);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const monthIndex = Number(isoMatch[2]) - 1;
    return buildLabel(year, monthIndex);
  }

  const ddMatch = ddMmYyyy.exec(raw);
  if (ddMatch) {
    const year = Number(ddMatch[3]);
    const monthIndex = Number(ddMatch[2]) - 1;
    return buildLabel(year, monthIndex);
  }

  return null;
}

function hasFilterValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (Array.isArray(value) && value.length === 0) {
    return false;
  }

  return true;
}

function mapIdsToNames(
  idsValue: unknown,
  dict: DictionaryDTO[] | undefined,
): string[] {
  if (idsValue == null || !dict?.length) {
    return [];
  }

  const rawIds = Array.isArray(idsValue) ? idsValue : [idsValue];
  const ids = rawIds.map((id) => String(id));

  return dict
    .filter((item) => ids.includes(String(item.id)))
    .map((item) => item.name)
    .filter((name): name is string => Boolean(name));
}

function buildMultiSelectChipLabel(
  fieldLabel: string,
  names: string[],
): string | null {
  if (!names.length) {
    return null;
  }

  if (names.length === 1) {
    return names[0];
  }

  return `${fieldLabel}: ${names.length}`;
}

function buildChipLabelFromField(
  key: string,
  value: FilterChipValue,
  field: FilterField | undefined,
): string | null {
  if (!field) {
    return null;
  }

  if (field.filterType === 'month') {
    const monthLabel = formatMonthLabelFromValue(value);
    return monthLabel;
  }

  const options = field.selectOptions ?? [];
  if (!options.length) {
    if (Array.isArray(value)) {
      if (field.filterType === 'date-range') {
        const [from, to] = value;
        if (!from && !to) {
          return null;
        }
        if (from && to) {
          return `${from} - ${to}`;
        }
      }

      return `${field.label}: ${value.length}`;
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    return `${field.label}: ${String(value)}`;
  }

  if (field.filterType === 'multi-select') {
    const names = mapIdsToNames(value, options);
    const label = buildMultiSelectChipLabel(field.label, names);
    if (label) {
      return label;
    }

    if (Array.isArray(value)) {
      return `${field.label}: ${value.length}`;
    }

    return null;
  }

  if (field.filterType === 'select') {
    const names = mapIdsToNames(value, options);
    if (!names.length) {
      if (Array.isArray(value)) {
        return `${field.label}: ${value.length}`;
      }

      if (value === null || value === undefined || value === '') {
        return null;
      }

      return `${field.label}: ${String(value)}`;
    }

    if (names.length === 1) {
      return names[0];
    }

    return buildMultiSelectChipLabel(field.label, names);
  }

  if (Array.isArray(value)) {
    return `${field.label}: ${value.length}`;
  }

  if (value === null || value === undefined || value === '') {
    return null;
  }

  return `${field.label}: ${String(value)}`;
}

export function buildActiveFilterChipsFromConfig(
  config: FiltersConfigLike | null | undefined,
  result: FilterResultLike | null | undefined,
): ActiveFilterChip[] {
  const filters: FilterValues = result?.filters ?? {};
  const groupingFormKey = 'group_by';
  const groupingValue = filters[groupingFormKey];

  const groupingChip: ActiveFilterChip | null = (() => {
    if (!config?.grouping) return null;
    if (!hasFilterValue(groupingValue)) return null;

    const allOptions = config.grouping.fields
      .flatMap((field) => field.options ?? [])
      .filter((option) => option.id != null);
    const selectedOption =
      allOptions.find((o) => String(o.id) === String(groupingValue)) ?? null;
    if (!selectedOption?.name) {
      return null;
    }

    return {
      key: groupingFormKey,
      label: `Группировка: ${selectedOption.name}`,
      required: config.grouping.required,
    };
  })();

  const fieldsByKey: Record<string, FilterField> = {};
  config?.filters.forEach((field) => {
    fieldsByKey[field.dataKey] = field;
  });

  const configKeys = (config?.filters ?? []).map((field) => field.dataKey);
  const orderedKeys = [
    ...configKeys,
    ...Object.keys(filters).filter(
      (key) => key !== groupingFormKey && !configKeys.includes(key),
    ),
  ];

  const otherChips = orderedKeys
    .map((key) => {
      const value = filters[key];
      if (!hasFilterValue(value)) {
        return null;
      }

      const field = fieldsByKey[key];
      const label = buildChipLabelFromField(
        key,
        value as FilterChipValue,
        field,
      );

      if (!label) {
        return null;
      }

      return {
        key,
        label,
        required: field?.required,
      };
    })
    .filter((chip): chip is ActiveFilterChip => chip !== null);

  if (!otherChips.length) {
    return groupingChip ? [groupingChip] : [];
  }

  return groupingChip ? [groupingChip, ...otherChips] : otherChips;
}
